<script>
import validationMessages from '@/components/validations'
import {required} from "vuelidate/lib/validators"
import Monitor from '@/services/Monitor'

export default {
  components: {
    validationMessages,
  },
  props: ['configGroups', 'configDefinitions', 'configKeys', 'definitionData'],
  data() {
    return {
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      key: '',
      group: '',
      description: '',
      keyTypes: ['string','integer','boolean','json'],
      type: 'string',
      tenant_required: 0,
      common_required: 0,
      is_secret: 0,
      csrf_token: localStorage.getItem('csrf_token')
    };
  },

  validations: {
    key: {required}
  },

  methods: {
    refreshData() {
      this.$emit('onRefresh')
    },

    closeModal() {
      this.showModal = false
    },

    resetProps() {
      this.group = ''
      this.key = ''
      this.description = ''
      this.type = 'string'
      this.tenant_required = 0
      this.common_required = 0
      this.is_secret = 0
      this.submitted = false
    },

    normalizeCheckboxValue(value) {
      return value === true || value === 1 || value === '1'
    },

    populateFromDefinition(definition = {}) {
      this.group = definition.group || ''
      this.key = definition.key || ''
      this.type = definition.type || 'string'
      this.description = definition.description || ''
      this.tenant_required = this.normalizeCheckboxValue(definition.tenant_required)
      this.common_required = this.normalizeCheckboxValue(definition.common_required)
      this.is_secret = this.normalizeCheckboxValue(definition.is_secret)
    },

    setProps() {
      this.populateFromDefinition(this.definitionData || {})
    },

    setPropsFromKey(key) {
      const definition = (this.configDefinitions || []).find(item => item.key === key)

      if (definition) {
        this.populateFromDefinition(definition)
      }
    },

    async editEnvConfigDefinition() {
      this.submitted = true
      this.tryingToSubmit = true

      this.$v.$touch()
      if (this.$v.$invalid) {
        this.tryingToSubmit = false
        return
      }

      const formData = {
        group: this.group,
        key: this.key,
        type: this.type,
        description: this.description,
        tenant_required: this.tenant_required ? 1 : 0,
        common_required: this.common_required ? 1 : 0,
        is_secret: this.is_secret ? 1 : 0
      }

      try {
        const response = await Monitor.editEnvConfigDefinition(formData)
        if (response.data.data) {
          this.successmsg('Environment Config Definition Changed!')
          this.refreshData()
          this.closeModal()
        } else {
          this.failedmsg(response.data.error || 'Failed to change Environment Config!')
        }
      } catch (error) {
        const resData = error.response?.data?.error || 'Failed'
        const errorMsg = Array.isArray(resData) ? resData[0] : resData
        this.failedmsg(errorMsg)
      } finally {
        this.tryingToSubmit = false
      }
    }

  },
  watch: {
    definitionData: {
      handler(newVal) {
        if (this.showModal) {
          this.populateFromDefinition(newVal || {})
        }
      },
      deep: true
    },

    key(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.setPropsFromKey(newVal)
      }
    }
  },
};
</script>

<template>
  <b-modal id="edit_config_definition" size="lg" v-model="showModal" @shown="setProps" @hidden="resetProps" title="Edit Environment Config Definition"
           title-class="font-18" :no-enforce-focus="true">
    <form @submit.prevent="editEnvConfigDefinition">
      <div class="row">
        <div class="col-12">
          <h6 class="text-uppercase mb-2">Key Definition</h6>
          <small class="text-muted d-block">
            Change information for key definition.
          </small>
        </div>

        <div class="col-12">
          <div class="mb-3">
            <label class="control-label form-label">Key Name *</label>
            <b-form-select
                v-model="key"
                :options="configKeys"
                class="w-100"
                :class="{ 'is-invalid': submitted && $v.key.$error }"
                :disabled="true"
            ></b-form-select>
            <validationMessages v-if="submitted" :fieldName="'Key Name'" :validationName="$v.key"></validationMessages>
          </div>

          <!-- Config Group -->
          <div class="row">
            <div class="col-9">
              <div class="mb-3">
                <label class="control-label form-label">Config Group (optional)</label>
                <input
                    v-model="group"
                    class="form-control"
                    list="groupOptions"
                    placeholder="Select or type a group"
                >
                <datalist id="groupOptions">
                  <option v-for="g in configGroups" :key="g" :value="g">{{ g }}</option>
                </datalist>
              </div>
            </div>

            <div class="col-3">
              <div class="mb-3">
                <label class="form-label">Key Type</label>
                <b-form-select
                    v-model="type"
                    :options="keyTypes"
                    class="w-100"
                ></b-form-select>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="control-label form-label">Key Description</label>
            <b-form-input v-model="description"></b-form-input>
          </div>

        </div>

        <div class="row">
          <div class="col-4">
            <b-form-checkbox-group>
              <div class="form-check form-switch form-switch-lg mb-3" >
                <input v-model="tenant_required"
                       type="checkbox"
                       class="form-check-input"
                       id="tenant_required"
                />
                <label class="form-check-label" for="tenant_required">Tenant Required</label>
              </div>
            </b-form-checkbox-group>
          </div>

          <div class="col-4">
            <b-form-checkbox-group>
              <div class="form-check form-switch form-switch-lg mb-3" >
                <input v-model="common_required"
                       type="checkbox"
                       class="form-check-input"
                       id="common_required"
                />
                <label class="form-check-label" for="common_required">Common Required</label>
              </div>
            </b-form-checkbox-group>
          </div>

          <div class="col-4">
            <b-form-checkbox-group>
              <div class="form-check form-switch form-switch-lg mb-3" >
                <input v-model="is_secret"
                       type="checkbox"
                       class="form-check-input"
                       id="is_secret"
                />
                <label class="form-check-label" for="is_secret">Is Secret</label>
              </div>
            </b-form-checkbox-group>
          </div>
        </div>

      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="editEnvConfigDefinition" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Submit
      </b-button>
    </template>
  </b-modal>
</template>
